import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import { useGetCountriesListQuery } from 'services/assets';
import { useGetBridgeKycInfoQuery, useGetMerchantQuery } from 'services/merchants';

import { SelectItem } from 'components/inputs/select/select';

import selectAccount, { selectAccountAccess } from 'state/selectors/accounts/accountSelector';
import { selectAccountType } from 'state/slices/accountSlice';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountAccessLevel, AccountType } from 'types/account';
import { UserAccessLevel } from 'types/user';

import { MerchantViewContainer } from './edit/container';
import { AssociatedPersonModal } from './edit/sections/associatedPerson/components/modal/associatedPersonModal';
import { KEYS as ASSOCIATED_PERSON_KEYS, AssociatedPersonsFormType } from './edit/sections/associatedPerson/keys';
import { mapTitanToFormData, useAssociatedPersonsApi } from './edit/sections/associatedPerson/useAssociatedPersonsApi';
import { useAssociatedPersonSessionForm } from './edit/sections/associatedPerson/useSessionForm';
import { CheckoutSectionContainer } from './edit/sections/checkout/container';
import CustodialWalletTransactionContainer from './edit/sections/custodialWalletTransaction/container';
import { GeneralSectionContainer } from './edit/sections/general/container';
import { AutoKybFlowProvider } from './edit/sections/kyb/AutoKybFlowContext';
import { KybSectionContainer } from './edit/sections/kyb/container';
import { KEYS, ModalKey } from './edit/sections/kyb/keys';
import { useAutoKybFlow } from './edit/sections/kyb/useAutoKybFlow';
import { useTitanData } from './edit/sections/kyb/useTitanData';
import PayoutReserveContainer from './edit/sections/payoutReserve/container';
import RefundAccountContainer from './edit/sections/refundAccount/container';
import { PayoutsSectionContainer } from './edit/sections/settlement/components/payouts/container';
import { SettlementContainer } from './edit/sections/settlement/container';
import { MerchantsListContainer } from './list/container';

export interface ModalStateProps {
  visibleModal: ModalKey | null;
  setVisibleModal: Dispatch<SetStateAction<ModalKey | null>>;
}

export interface SetModalProps {
  setVisibleModal: Dispatch<SetStateAction<ModalKey | null>>;
}

export const MerchantsListRoute = MerchantsListContainer;

export const MerchantViewRoute = MerchantViewContainer;

export const MerchantGeneralSectionRoute = () => {
  const [visibleModal, setVisibleModal] = useState<ModalKey | null>(null);
  const [isAssociatedPersonModalOpen, setIsAssociatedPersonModalOpen] = useState(false);

  const matchId = useMatch(KEYS.ROUTE_MATCH_ID);
  const match = useMatch(KEYS.ROUTE_MATCH);
  const selectedMatch = matchId || match;
  const { account } = useSelector(selectAccount);
  const { uuid } = selectedMatch?.params ?? { uuid: account.uuid };
  const { data: merchantResponse } = useGetMerchantQuery(uuid!, { skip: !uuid });
  const { data: bridgeKycInfo } = useGetBridgeKycInfoQuery(uuid!, { skip: !uuid });
  const { data: countries = [] } = useGetCountriesListQuery();

  const userLevel = useSelector(selectUserLevel);
  const accountType = useSelector(selectAccountType);
  const accountAccessLevel = useSelector(selectAccountAccess);
  const isViewer = accountAccessLevel === AccountAccessLevel.Viewer;
  const isAdmin = Boolean(userLevel && userLevel >= UserAccessLevel.Admin);
  const isPartner = userLevel === UserAccessLevel.Partner;
  const isPartnerViaPspOrGroupAccount = isPartner && (accountType === AccountType.PSP || accountType === AccountType.Pwc_Group);
  const shouldShowPanel = Boolean(merchantResponse?.data) && !isViewer && (isAdmin || isPartnerViaPspOrGroupAccount);

  const { run, isProcessing } = useAutoKybFlow({ uuid });
  const { fetchTitanData, titanData, isLoadingTitanData } = useTitanData({
    shift4MerchantId: merchantResponse?.data?.shift4MerchantId,
    includeAllOwners: true,
  });

  const associatedPersonSessionControllers = useAssociatedPersonSessionForm(ASSOCIATED_PERSON_KEYS.STORAGE_KEY);
  const associatedPersonsApi = useAssociatedPersonsApi(uuid);

  const originalAssociatedPersonsData = useRef<AssociatedPersonsFormType | null>(null);

  const mappedCountries: SelectItem[] = countries.map((country) => ({
    key: country.isoAlpha3,
    icon: country.iconUrl,
    label: country.name,
  }));

  const handleOpenKyb = async () => {
    if (shouldShowPanel && merchantResponse?.data?.shift4MerchantId) {
      await fetchTitanData();
    }
    setVisibleModal(KEYS.KYB);
  };

  const handleOpenAssociatedPersonModal = useCallback(async () => {
    if (shouldShowPanel && merchantResponse?.data?.shift4MerchantId) {
      await fetchTitanData();
    }
    associatedPersonsApi.clearErrors();

    if (associatedPersonSessionControllers.hasSessionData()) {
      associatedPersonSessionControllers.refreshSessionValues();

      if (!originalAssociatedPersonsData.current) {
        const existingData = await associatedPersonsApi.fetchAssociatedPersons();
        originalAssociatedPersonsData.current = existingData;
      }
    } else {
      const existingData = await associatedPersonsApi.fetchAssociatedPersons();
      originalAssociatedPersonsData.current = existingData;

      associatedPersonSessionControllers.setFormValues(existingData);
    }

    setIsAssociatedPersonModalOpen(true);
  }, [associatedPersonsApi, associatedPersonSessionControllers]);

  const handleCloseAssociatedPersonModal = () => {
    setIsAssociatedPersonModalOpen(false);
  };

  const handleConfirmAssociatedPerson = async (payload: AssociatedPersonsFormType) => {
    if (!originalAssociatedPersonsData.current) {
      return;
    }

    const result = await associatedPersonsApi.submitChanges(payload, originalAssociatedPersonsData.current);

    if (result.success) {
      associatedPersonSessionControllers.resetSessionForm();
      handleCloseAssociatedPersonModal();
    }
  };

  const handleDeleteAssociatedPerson = async (personUuid: string) => {
    const result = await associatedPersonsApi.deletePersonById(personUuid);

    if (result.success && originalAssociatedPersonsData.current) {
      originalAssociatedPersonsData.current = {
        ...originalAssociatedPersonsData.current,
        beneficiaries: originalAssociatedPersonsData.current.beneficiaries.filter((b) => b.uuid !== personUuid),
      };
    }

    return result;
  };

  const associatedPersonsTitanData = useMemo(() => {
    const persons = titanData?.data?.associatedPersons;
    if (!persons || persons.length === 0) return undefined;
    return mapTitanToFormData(persons);
  }, [titanData]);

  const onInsertTitanData = useCallback(() => {
    if (!associatedPersonsTitanData) return;
    associatedPersonSessionControllers.setFormValues(associatedPersonsTitanData);
  }, [associatedPersonsTitanData, associatedPersonSessionControllers]);

  return (
    <AutoKybFlowProvider value={{ run, isProcessing, titanData, isLoadingTitanData }}>
      <KybSectionContainer
        visibleModal={visibleModal}
        setVisibleModal={setVisibleModal}
        autoKybFlowProps={{ run, isProcessing, titanData, isLoadingTitanData, fetchTitanData }}
        shouldShowPanel={shouldShowPanel}
        openAssociatedPersonModal={handleOpenAssociatedPersonModal}
      />
      <GeneralSectionContainer
        setVisibleModal={setVisibleModal}
        onOpenKyb={handleOpenKyb}
        onOpenAssociatedPerson={handleOpenAssociatedPersonModal}
      />
      <CheckoutSectionContainer />
      <AssociatedPersonModal
        merchantUuid={uuid}
        bridgeKycInfo={bridgeKycInfo}
        titanProps={{ titanFormData: associatedPersonsTitanData, isLoadingTitanData, fetchTitanData, onInsertTitanData }}
        onConfirm={handleConfirmAssociatedPerson}
        onDeletePerson={handleDeleteAssociatedPerson}
        isOpen={isAssociatedPersonModalOpen}
        closeModal={handleCloseAssociatedPersonModal}
        countries={mappedCountries}
        error={associatedPersonsApi.error}
        isSubmitting={associatedPersonsApi.isSubmitting}
        isDeleting={associatedPersonsApi.isDeleting}
        isLoading={associatedPersonsApi.isLoading}
        clearBackendErrors={associatedPersonsApi.clearErrors}
        sessionControllers={associatedPersonSessionControllers}
      />
    </AutoKybFlowProvider>
  );
};

export const SettlementSectionRoute = () => (
  <>
    <SettlementContainer />
    <PayoutsSectionContainer />
  </>
);

export const MerchantRefundAccountRoute = RefundAccountContainer;
export const MerchantPayoutReserveRoute = PayoutReserveContainer;

export const MerchantRefundAccountTransactionRoute = CustodialWalletTransactionContainer;
